import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import DynamicCodeEditor from "../codeEditor/DynamicCodeEditor";
import JSCodeEditorInput from "../codeEditor/JSCodeEditorInput";
import PythonCodeEditorInput from "../codeEditor/PythonCodeEditorInput";
import HtmlCodeEditorInput from "../codeEditor/HtmlCodeEditorInput";
import SqlCodeEditorInput from "../codeEditor/SqlCodeEditorInput";

export default function EditableCodeSection({
  code,
  language,
  index,
  isAdmin,
  onEdit,
  onDelete,
  onSave,
  onAdd, // Callback to add a new section after this one
  autoEdit = false, // Auto-open edit mode for newly added sections
  onEditComplete, // Callback when edit is complete
}) {
  const [isEditing, setIsEditing] = useState(autoEdit); // Start in edit mode if autoEdit is true
  const [editedCode, setEditedCode] = useState(code);
  const [editedLanguage, setEditedLanguage] = useState(language);
  // Use ref to store the latest code value to avoid stale closures
  const editedCodeRef = useRef(code);
  const editedLanguageRef = useRef(language);
  // Use a counter to force re-render when entering edit mode
  const [editKey, setEditKey] = useState(0);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Update refs whenever editedCode/editedLanguage changes
  useEffect(() => {
    editedCodeRef.current = editedCode;
    editedLanguageRef.current = editedLanguage;
  }, [editedCode, editedLanguage]);

  // Update state when code/language changes from props (e.g., after refresh)
  // Only update when NOT editing to avoid overwriting user's changes
  useEffect(() => {
    if (!isEditing) {
      // Sync with latest props when not editing
      const codeToSet = code || "";
      const langToSet = language || "text";
      setEditedCode(codeToSet);
      setEditedLanguage(langToSet);
      editedCodeRef.current = codeToSet;
      editedLanguageRef.current = langToSet;
    }
  }, [code, language, isEditing]);

  // Initialize editedCode when entering edit mode for the first time
  useEffect(() => {
    if (isEditing && autoEdit) {
      // When auto-editing (newly added section), ensure editedCode matches code
      setEditedCode(code);
      setEditedLanguage(language);
      editedCodeRef.current = code;
      editedLanguageRef.current = language;
    }
  }, [autoEdit, isEditing, code, language]);

  // Auto-open edit mode if autoEdit is true
  useEffect(() => {
    if (autoEdit && !isEditing) {
      setIsEditing(true);
    }
  }, [autoEdit, isEditing]);

  const handleEdit = () => {
    console.log("handleEdit called:", { code, language, index, isEditing, codeLength: code?.length });
    // Always sync with latest props before editing
    const codeToEdit = code || "";
    const langToEdit = language || "text";

    // Set state and refs immediately
    setEditedCode(codeToEdit);
    setEditedLanguage(langToEdit);
    editedCodeRef.current = codeToEdit;
    editedLanguageRef.current = langToEdit;

    // Increment editKey to force editor re-render with new initialCode
    setEditKey(prev => prev + 1);

    // Set editing mode immediately - the key prop will force re-render
    setIsEditing(true);
  };

  const handleSave = () => {
    // Use ref to get the latest value (avoids stale closure issues)
    // Also check if editedCode is explicitly null/undefined, not just falsy (empty string is valid)
    const codeToSave = editedCodeRef.current !== null && editedCodeRef.current !== undefined
      ? editedCodeRef.current
      : code;
    const langToSave = editedLanguageRef.current !== null && editedLanguageRef.current !== undefined
      ? editedLanguageRef.current
      : language;

    // Debug log to verify we're saving the correct code
    console.log("Saving code section:", {
      index,
      codeToSave,
      langToSave,
      editedCode: editedCodeRef.current,
      editedLanguage: editedLanguageRef.current,
      code,
      language
    });

    if (onSave) {
      onSave(index, codeToSave, langToSave);
    }
    setIsEditing(false);
    if (onEditComplete) {
      onEditComplete();
    }
  };

  const handleCancel = () => {
    setEditedCode(code);
    setEditedLanguage(language);
    editedCodeRef.current = code;
    editedLanguageRef.current = language;
    setIsEditing(false);
    if (onEditComplete) {
      onEditComplete();
    }
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this code section?")) {
      if (onDelete) {
        onDelete(index);
      }
    }
  };

  if (!isAdmin) {
    // Non-admin view - just display the code
    return (
      <Box sx={{ position: "relative", my: 2 }}>
        <DynamicCodeEditor language={language} initialCode={code} />
      </Box>
    );
  }

  // Admin view - with edit/delete buttons
  return (
    <Box
      data-code-section-index={index}
      sx={{
        position: "relative",
        my: 2,
        border: isEditing
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: isEditing ? 1 : 0,
        backgroundColor: isEditing
          ? theme.palette.action.hover
          : "transparent",
        transition: "all 0.3s ease",
      }}
    >
      {/* Admin Controls */}
      {!isEditing && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            gap: 0.5,
            zIndex: 10,
            backgroundColor: isDark
              ? "rgba(0,0,0,0.7)"
              : "rgba(255,255,255,0.9)",
            borderRadius: 1,
            p: 0.5,
          }}
        >
          <Tooltip title="Add Code Section After This">
            <IconButton
              size="small"
              onClick={() => onAdd && onAdd(index)}
              sx={{
                color: theme.palette.success.main,
                "&:hover": {
                  backgroundColor: theme.palette.success.light,
                  color: "white",
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Code Section">
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log("Edit button clicked, current state:", { isEditing, code, editedCode });
                handleEdit();
              }}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                  color: "white",
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Code Section">
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{
                color: theme.palette.error.main,
                "&:hover": {
                  backgroundColor: theme.palette.error.light,
                  color: "white",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Code Display/Editor */}
      {isEditing ? (
        <Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Language</InputLabel>
            <Select
              value={editedLanguage}
              onChange={(e) => setEditedLanguage(e.target.value)}
              label="Language"
            >
              <MenuItem value="javascript">JavaScript</MenuItem>
              <MenuItem value="html">HTML</MenuItem>
              <MenuItem value="css">CSS</MenuItem>
              <MenuItem value="python">Python</MenuItem>
              <MenuItem value="fastapi">FastAPI</MenuItem>
              <MenuItem value="sql">SQL</MenuItem>
              <MenuItem value="text">Text</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ mb: 2, minHeight: "200px", border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
            {editedLanguage === "javascript" && (
              <JSCodeEditorInput
                key={`js-editor-${index}-${isEditing ? 'edit' : 'view'}-${editKey}`} // Force re-render when entering edit mode
                initialCode={editedCode || ""}
                onCodeChange={(value) => {
                  console.log("Code changed:", { index, value, length: value.length });
                  setEditedCode(value);
                  editedCodeRef.current = value; // Update ref immediately
                }}
                editable={true}
              />
            )}
            {editedLanguage === "python" && (
              <PythonCodeEditorInput
                key={`py-editor-${index}-${isEditing ? 'edit' : 'view'}-${editKey}`}
                initialCode={editedCode || ""}
                onCodeChange={(value) => {
                  console.log("Code changed:", { index, value, length: value.length });
                  setEditedCode(value);
                  editedCodeRef.current = value; // Update ref immediately
                }}
                editable={true}
              />
            )}
            {editedLanguage === "html" && (
              <HtmlCodeEditorInput
                key={`html-editor-${index}-${isEditing ? 'edit' : 'view'}-${editKey}`}
                initialCode={editedCode || ""}
                onCodeChange={(value) => {
                  console.log("Code changed:", { index, value, length: value.length });
                  setEditedCode(value);
                  editedCodeRef.current = value; // Update ref immediately
                }}
                editable={true}
              />
            )}
            {editedLanguage === "css" && (
              <HtmlCodeEditorInput
                key={`css-editor-${index}-${isEditing ? 'edit' : 'view'}-${editKey}`}
                initialCode={editedCode || ""}
                onCodeChange={(value) => {
                  console.log("Code changed:", { index, value, length: value.length });
                  setEditedCode(value);
                  editedCodeRef.current = value; // Update ref immediately
                }}
                editable={true}
              />
            )}
            {editedLanguage === "sql" && (
              <SqlCodeEditorInput
                key={`sql-editor-${index}-${isEditing ? 'edit' : 'view'}-${editKey}`}
                initialCode={editedCode || ""}
                onCodeChange={(value) => {
                  console.log("Code changed:", { index, value, length: value.length });
                  setEditedCode(value);
                  editedCodeRef.current = value; // Update ref immediately
                }}
                editable={true}
              />
            )}
            {!["javascript", "python", "html", "css", "sql"].includes(editedLanguage) && (
              <DynamicCodeEditor
                key={`dynamic-editor-${index}-${isEditing ? 'edit' : 'view'}-${editKey}`}
                language={editedLanguage}
                initialCode={editedCode || ""}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        </Box>
      ) : (
        <DynamicCodeEditor language={language} initialCode={code} />
      )}
    </Box>
  );
}

